import { format } from 'date-fns';
import {
  ApiSearchSelect,
  DateSearchSelect,
  InputSearch,
  MultipleSearchSelect
} from '@/components/custom/erp/data-table-v2/search-inputs';
import { SearchSearchColumnDef } from '@/components/custom/erp';
import type { InputTableColumn } from '@/components/custom/erp';
import { formatMoney } from '@/lib/formatUtils';
import type { PhieuChi } from '@/types/schemas';
import { QUERY_KEYS } from '@/constants';

const STATUS_OPTIONS = [
  { value: '0', label: 'Chưa ghi sổ' },
  { value: '3', label: 'Chờ duyệt' },
  { value: '5', label: 'Đã ghi sổ' },
  { value: '6', label: 'Đang thực hiện' },
  { value: '7', label: 'Hoàn thành' }
];

const MA_NGV_OPTIONS = [
  { value: '1', label: '1. Chi theo hóa đơn' },
  { value: '2', label: '2. Chi theo đối tượng' },
  { value: '3', label: '3. Chi khác' },
  { value: '4', label: '4. Gửi tiền vào ngân hàng' }
];

export const getDataTableColumns = (
  handleViewClick: (row: PhieuChi) => void,
  totalAmount?: number | null
): SearchSearchColumnDef<PhieuChi>[] => [
  {
    field: 'status',
    headerName: 'Trạng thái',
    width: 120,
    renderCell: params => {
      const status = params.row.status;
      const option = STATUS_OPTIONS.find(opt => opt.value === status);
      return option?.label;
    },
    renderSearchInput: params => <MultipleSearchSelect {...params} options={STATUS_OPTIONS} />
  },
  {
    field: 'so_ct',
    headerName: 'Số chứng từ',
    width: 135,
    renderCell: params => (
      <button
        onClick={e => {
          e.stopPropagation();
          handleViewClick(params.row);
        }}
        className='hover:text-blue-500 hover:underline'
      >
        {params.row.so_ct}
      </button>
    )
  },
  {
    field: 'ngay_ct',
    headerName: 'Ngày chứng từ',
    width: 120,
    renderCell: params => {
      if (!params.row.ngay_ct) return '';
      try {
        const date = new Date(params.row.ngay_ct);
        if (isNaN(date.getTime())) return params.row.ngay_ct;
        return format(date, 'dd/MM/yyyy');
      } catch {
        return params.row.ngay_ct || '';
      }
    },
    renderSearchInput: params => <DateSearchSelect {...params} />
  },
  {
    field: 'ma_kh',
    headerName: 'Mã khách hàng',
    width: 120,
    renderCell: params => params.row.ma_kh_data?.customer_code,
    renderSearchInput: params => (
      <ApiSearchSelect
        {...params}
        apiEndpoint={QUERY_KEYS.AUTOCOMPLETE_DATA}
        model='PHIEU_CHI'
        searchField='ma_kh'
        columns={[
          { key: 'customer_code', label: 'Mã khách hàng', width: '120px' },
          { key: 'customer_name', label: 'Tên khách hàng', width: '200px' },
          { key: 'tax_code', label: 'Diễn giải', width: '100px' }
        ]}
        valueKey='customer_code'
        displayKey='customer_code'
      />
    )
  },
  {
    field: 'ten_kh',
    headerName: 'Tên khách hàng',
    width: 200,
    renderCell: params => params.row.ma_kh_data?.customer_name,
    renderSearchInput: params => (
      <ApiSearchSelect
        {...params}
        apiEndpoint={QUERY_KEYS.AUTOCOMPLETE_DATA}
        model='PHIEU_CHI'
        searchField='ma_kh'
        columns={[
          { key: 'customer_code', label: 'Mã khách hàng', width: '120px' },
          { key: 'customer_name', label: 'Tên khách hàng', width: '200px' },
          { key: 'tax_code', label: 'Diễn giải', width: '100px' }
        ]}
        valueKey='customer_name'
        displayKey='customer_name'
      />
    )
  },
  {
    field: 'dien_giai',
    headerName: 'Diễn giải',
    width: 300,
    renderCell: params => params.row.dien_giai
  },
  {
    field: 'tk',
    headerName: 'Tài khoản có',
    width: 100,
    renderCell: params => params.row.tk_data?.code,
    renderSearchInput: params => (
      <ApiSearchSelect
        {...params}
        apiEndpoint={QUERY_KEYS.AUTOCOMPLETE_DATA}
        model='PHIEU_CHI'
        searchField='tk'
        columns={[
          { key: 'code', label: 'Mã tài khoản', width: '120px' },
          { key: 'name', label: 'Tên tài khoản', width: '200px' }
        ]}
        valueKey='code'
        displayKey='code'
      />
    )
  },
  {
    field: 't_tien',
    headerName: 'Tổng tiền',
    width: 150,
    type: 'number',
    align: 'right',
    renderCell: params => formatMoney(params.row.t_tien),
    renderSearchInput: params => (
      <InputSearch
        {...params}
        onKeyDown={params.onKeyDown ?? (() => {})}
        suffixContent={
          <div className='flex items-center gap-2'>
            <span className='text-lg font-bold'>&#931;</span>
            <span className='font-semibold text-main'>
              {typeof totalAmount === 'number' ? formatMoney(totalAmount) : '—'}
            </span>
          </div>
        }
      />
    )
  },
  {
    field: 'ma_nt',
    headerName: 'Ngoại tệ',
    width: 120,
    renderCell: params => params.row.ma_nt_data?.ma_nt
  },

  {
    field: 'ma_ngv',
    headerName: 'Loại phiếu chi',
    width: 190,
    renderCell: params => {
      const status = params.row.ma_ngv;
      const option = MA_NGV_OPTIONS.find(opt => opt.value === status);
      return option?.label;
    },
    renderSearchInput: params => <MultipleSearchSelect {...params} options={MA_NGV_OPTIONS} />
  }
];

export const getInputTableColumns = (): InputTableColumn[] => [
  {
    field: 'dien_giai',
    headerName: 'Diễn giải',
    width: 300,
    renderCell: params => params.row.dien_giai
  },
  {
    field: 'ma_kh',
    headerName: 'Mã đối tượng',
    width: 120,
    renderCell: params => params.row.ma_kh_data?.customer_code
  },
  {
    field: 'ten_kh',
    headerName: 'Tên đối tượng',
    width: 200,
    renderCell: params => params.row.ma_kh_data?.customer_name
  },
  // {
  //   field: 'du_cn',
  //   headerName: 'Dư công nợ',
  //   width: 120,
  //   renderCell: params => <CellField name='du_cn' value={params.row.ma_kh_data?.du_cn || 0} type='number' />
  // },
  {
    field: 'tk_no',
    headerName: 'Tài khoản nợ',
    width: 120,
    renderCell: params => params.row.tk_no_data?.code
  },
  {
    field: 'tien_nt',
    headerName: 'Tiền VND',
    width: 150,
    type: 'number',
    renderCell: params => formatMoney(params.row.tien_nt)
  },
  {
    field: 'ma_loai_hd',
    headerName: 'Loại hóa đơn',
    width: 180,
    renderCell: params => {
      const value = params.row.ma_loai_hd;
      switch (value) {
        case '0':
          return '0. Không có hoá đơn';
        case '1':
          return '1. Hoá đơn GTGT ';
        case '2':
          return '2. Hoá đơn bán hàng thông thường';
        default:
          return '';
      }
    }
  },
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 120,
    renderCell: params => params.row.ma_bp_data?.ma_bp
  },

  {
    field: 'ma_vv',
    headerName: 'Vụ việc',
    width: 120,
    renderCell: params => params.row.ma_vv_data?.ma_vu_viec || params.row.ma_vv_data?.code
  },

  {
    field: 'ma_hd',
    headerName: 'Hợp đồng',
    width: 120,
    renderCell: params => params.row.ma_hd_data?.ma_hd
  },

  {
    field: 'ma_dtt',
    headerName: 'Đợt thanh toán',
    width: 150,
    renderCell: params => params.row.ma_dtt_data?.code
  },

  {
    field: 'ma_ku',
    headerName: 'Khế ước',
    width: 120,
    renderCell: params => params.row.ma_ku_data?.ma_ku || params.row.ma_ku_data?.code
  },

  {
    field: 'ma_phi',
    headerName: 'Phí',
    width: 120,
    renderCell: params => params.row.ma_phi_data?.ma_phi || params.row.ma_phi_data?.code
  },

  {
    field: 'ma_sp',
    headerName: 'Sản phẩm',
    width: 120,
    renderCell: params => params.row.ma_sp_data?.ma_vt || params.row.ma_sp_data?.code
  },

  {
    field: 'ma_cp0',
    headerName: 'Chi phí không hợp lệ',
    width: 180,
    renderCell: params => params.row.ma_cp0_data?.ma_cpkhl
  }
];

export const historyColumns: InputTableColumn[] = [
  { field: 'username', headerName: 'Tài khoản', width: 100 },
  { field: 'nickname', headerName: 'Tên tài khoản', width: 200 },
  { field: 'action', headerName: 'Hành động', width: 100 },
  {
    field: 'datetime0',
    headerName: 'Thời gian',
    width: 150,
    renderCell: params => {
      if (!params.row.datetime0) return '';
      try {
        const date = new Date(params.row.datetime0);
        if (isNaN(date.getTime())) return params.row.datetime0;
        return format(date, 'dd/MM/yyyy HH:mm');
      } catch {
        return params.row.datetime0 || '';
      }
    }
  }
];
